import { useSessionStorage } from '@vueuse/core'
import { useOverlays } from '~/composables/useOverlays'
import type { SbGlobalBanner } from '~/storyblok/types/storyblok'
import { useDataDogTest } from '~/composables/useDataDogTest'

export const useBanners = () => {
  const overlays = useOverlays('banner')
  const { isDataDogTest } = useDataDogTest()
  const { data, fetch } = overlays

  /**
   * Keep track of closed banners in session storage
   */
  const localKey = 'bannersLastCloseAt'
  const closed: any = import.meta.client
    ? useSessionStorage(localKey, {} as Record<string, string>)
    : undefined

  const now = ref(new Date())
  let countdownTimerId: NodeJS.Timeout | undefined

  const updateTime = () => {
    now.value = new Date()
    countdownTimerId = setTimeout(updateTime, 60000)
  }

  const isTimeBasedBanner = (banner: SbGlobalBanner) =>
    isDefined(banner.start_time) &&
    isDefined(banner.end_time) &&
    banner.start_time !== '' &&
    banner.end_time !== ''

  const hasTimeBasedBanners = computed(() =>
    data.value?.some((banner) => isTimeBasedBanner(banner.content)),
  )

  const shouldBeVisible = (
    banner: SbGlobalBanner & { published_at?: string },
    uuid: string,
  ): boolean => {
    if (isDataDogTest.value) {
      return false
    }

    if ('hidden' in banner && banner.hidden) {
      return false
    }

    if (isTimeBasedBanner(banner)) {
      const startTime = new Date(String(banner.start_time))
      const endTime = new Date(String(banner.end_time))

      if (endTime < startTime) {
        return false
      }

      if (now.value < startTime || now.value > endTime) {
        return false
      }
    }

    if (import.meta.client && closed?.value) {
      const lastClosedAt = closed.value[uuid]
      if (banner.published_at && lastClosedAt) {
        return Date.parse(banner.published_at) > Number.parseInt(lastClosedAt)
      }
    }
    return true
  }

  const close = (uuid: string) => {
    if (import.meta.client && closed?.value) {
      closed.value[uuid] = Date.now().toString()
    }
  }

  // Watch to start or stop the countdown timer
  if (import.meta.client) {
    watch(
      hasTimeBasedBanners,
      (hasBanners) => {
        if (hasBanners && !countdownTimerId) {
          updateTime()
        } else if (!hasBanners && countdownTimerId) {
          clearTimeout(countdownTimerId)
          countdownTimerId = undefined
        }
      },
      { immediate: true },
    )

    onBeforeUnmount(() => {
      if (countdownTimerId) {
        clearTimeout(countdownTimerId)
      }
    })
  }

  return extendPromise(
    overlays.then(() => ({})),
    {
      banners: data,
      fetch,
      shouldBeVisible,
      close,
    },
  )
}
